import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import { classRegistry, FabricObject } from 'fabric';
import { EMPTY_CHART_CONFIG, MnemonicChartConfig, resolveChartSeriesNameFromConfig, toEChartsOption } from './mnemonic-chart.mapper';
import {
    createOffscreenEChartsContainer,
    drawEChartsCanvas,
    resolveMnemonicEChartsRenderScale,
    resolveMnemonicEChartsSize,
} from './mnemonic-echarts-host.util';

const CHART_PROPS = ['chart'] as const;

export class MnemonicChart extends FabricObject {
    declare chart: MnemonicChartConfig;
    declare name: string;

    private chartInstance?: ECharts;
    private chartContainer?: HTMLDivElement;
    private renderScale = 2;

    static override getDefaults() {
        return {
            ...super.getDefaults(),
            ...MnemonicChart.ownDefaults,
        };
    }

    constructor(options: Record<string, unknown> = {}) {
        super();
        Object.assign(this, MnemonicChart.ownDefaults);
        this.setOptions(options);
        this.chart = (options.chart as MnemonicChartConfig) ?? { ...EMPTY_CHART_CONFIG };
        this.createChartInstance();
    }

    updateChartConfig(chartConfig: MnemonicChartConfig): void {
        this.chart = {
            ...this.chart,
            ...chartConfig,
            data: chartConfig.data ?? this.chart.data,
            options: {
                ...this.chart.options,
                ...chartConfig.options,
            },
        };
        this.syncChartInstance();
    }

    override set(key: string | Record<string, unknown>, value?: unknown): this {
        const chartPayload =
            typeof key === 'object' && key !== null && 'chart' in key
                ? (key.chart as MnemonicChartConfig)
                : key === 'chart'
                  ? (value as MnemonicChartConfig)
                  : null;

        const result = super.set(key as never, value as never);

        if (chartPayload) {
            this.updateChartConfig(chartPayload);
        }

        if (
            (typeof key === 'object' && key !== null && ('width' in key || 'height' in key || 'scaleX' in key || 'scaleY' in key)) ||
            key === 'width' ||
            key === 'height' ||
            key === 'scaleX' ||
            key === 'scaleY'
        ) {
            this.resizeChart();
        }

        return result;
    }

    override toObject(propertiesToInclude: string[] = []): Record<string, unknown> {
        return super.toObject([...CHART_PROPS, ...propertiesToInclude]);
    }

    override _render(ctx: CanvasRenderingContext2D): void {
        this.ensureRenderResolution();

        const width = this.width;
        const height = this.height;
        const x = -width / 2;
        const y = -height / 2;

        ctx.fillStyle = (this.fill as string) || '#ffffff';
        ctx.fillRect(x, y, width, height);

        const chartCanvas = this.getChartCanvas();
        if (chartCanvas) {
            drawEChartsCanvas(ctx, chartCanvas, x, y, width, height);
        }
    }

    override dispose(): void {
        this.destroyChartInstance();
        super.dispose();
    }

    static override async fromObject(object: Record<string, unknown>): Promise<MnemonicChart> {
        return new MnemonicChart(object);
    }

    private createChartInstance(): void {
        this.destroyChartInstance();

        const { width, height } = resolveMnemonicEChartsSize(this);
        this.renderScale = resolveMnemonicEChartsRenderScale(this);
        this.chartContainer = createOffscreenEChartsContainer(width, height);

        this.chartInstance = echarts.init(this.chartContainer, undefined, {
            renderer: 'canvas',
            width,
            height,
            devicePixelRatio: this.renderScale,
        });

        this.chartInstance.setOption(toEChartsOption(this.chart, this.resolveChartSeriesName(), width, height), true);
        this.dirty = true;
    }

    private ensureRenderResolution(): void {
        const nextScale = resolveMnemonicEChartsRenderScale(this);

        if (nextScale === this.renderScale && this.chartInstance) {
            return;
        }

        this.renderScale = nextScale;

        if (!this.chartContainer) {
            return;
        }

        const { width, height } = resolveMnemonicEChartsSize(this);
        this.chartInstance?.dispose();
        this.chartInstance = echarts.init(this.chartContainer, undefined, {
            renderer: 'canvas',
            width,
            height,
            devicePixelRatio: this.renderScale,
        });
        this.chartInstance.setOption(toEChartsOption(this.chart, this.resolveChartSeriesName(), width, height), true);
        this.dirty = true;
    }

    private syncChartInstance(): void {
        if (!this.chartInstance) {
            this.createChartInstance();
            this.canvas?.requestRenderAll();
            return;
        }

        const { width, height } = resolveMnemonicEChartsSize(this);
        this.chartInstance.setOption(toEChartsOption(this.chart, this.resolveChartSeriesName(), width, height), true);
        this.dirty = true;
        this.canvas?.requestRenderAll();
    }

    private resizeChart(): void {
        if (!this.chartInstance || !this.chartContainer) {
            return;
        }

        const { width, height } = resolveMnemonicEChartsSize(this);
        const nextScale = resolveMnemonicEChartsRenderScale(this);

        this.chartContainer.style.width = `${width}px`;
        this.chartContainer.style.height = `${height}px`;

        if (nextScale !== this.renderScale) {
            this.renderScale = nextScale;
            this.chartInstance.dispose();
            this.chartInstance = echarts.init(this.chartContainer, undefined, {
                renderer: 'canvas',
                width,
                height,
                devicePixelRatio: this.renderScale,
            });
            this.chartInstance.setOption(toEChartsOption(this.chart, this.resolveChartSeriesName(), width, height), true);
        } else {
            this.chartInstance.resize({ width, height });
            this.chartInstance.setOption(toEChartsOption(this.chart, this.resolveChartSeriesName(), width, height), true);
        }

        this.dirty = true;
    }

    private resolveChartSeriesName(): string {
        return resolveChartSeriesNameFromConfig(this.chart, this.name);
    }

    private getChartCanvas(): HTMLCanvasElement | null {
        return (this.chartInstance?.getDom().querySelector('canvas') as HTMLCanvasElement | null) ?? null;
    }

    private destroyChartInstance(): void {
        this.chartInstance?.dispose();
        this.chartInstance = undefined;
        this.chartContainer?.remove();
        this.chartContainer = undefined;
    }
}

Object.defineProperty(MnemonicChart, 'type', { value: 'chart' });
Object.defineProperty(MnemonicChart, 'ownDefaults', {
    value: {
        width: 360,
        height: 240,
        fill: '#ffffff',
        stroke: null,
        strokeWidth: 0,
        originX: 'left',
        originY: 'top',
        objectCaching: false,
    },
});
Object.defineProperty(MnemonicChart, 'customProperties', {
    value: ['chart', 'id', 'name', 'objectType', 'tooltip', 'deviceId', 'variableId', 'selectOption'],
});

let isChartClassRegistered = false;

export function registerMnemonicChartClass(): void {
    if (isChartClassRegistered) {
        return;
    }

    classRegistry.setClass(MnemonicChart, 'chart');
    isChartClassRegistered = true;
}
